// Generic Data Table VM meant to be used with display-table

import KOBindings from './ko-bindings';
import Column from './column';
import Row from './row';

export default class DataTable {
  constructor(data, node, options) {
    this.defaults = {
      // A dataset name is required to grab the rows
      // Visible columns are handled by the parent VM
      dataset_name: 'table_data',
      table_properties: {
        creatable: { enabled: false },
        paginatable: { enabled: false },
        searchable: { enabled: false }
      },
      // Columns should be an array of objects with specific properties like:
      // name, property, type, sortable options etc.
      // See table-vm in the style guide example for more details
      columns: [],
      column_properties: {
        hideable: { enabled: false },
        // For sorting, a default column should be provided
        // Initial data order should be handled by the backend
        sortable: { enabled: false },
        editable: { enabled: false }
      },
      row_properties: {
        removable: { enabled: false },
        // To setup reordering, there should be a drag override column if columns are sortable
        draggable: { enabled: false, callbacks: {} },
        data_url: '',
        actions: 3
      }
    };
    this.config = _.extend({}, this.defaults, options);
    data = ko.mapping.fromJS(data);
    this.totals = ko.mapping.fromJS(data['total_' + this.config.dataset_name]);
    this.actions = ko.observable(this.config.row_properties.actions);
    this.$el = $(node);
    this.$tbody = $(this.$el).find('.kd-data-table__data');
    this.createObservables(data, node);
    this.setupSubscriptions();
    if (this.config.row_properties.draggable.enabled) {
      Row.initDraggable(this.$tbody, this.config.row_properties.draggable.update_url, this);
      //this.config.row_properties.draggable.manual_order = false;

      if (!_.isEmpty(this.config.row_properties.draggable.callbacks)) {
        Row.initDraggableCallbacks(this.$tbody, this.config.row_properties.draggable.callbacks);
      }
    }
    this.setUserOptions();
  }

  ///////////////////////////////////
  // METHODS TO OVERRIDE IN PARENT VM
  ///////////////////////////////////

  prepRowData(data){
    // Called before Row data is mapped
  }

  cleanRowSortingData(row, arr){
    // Called before Row data is sent to backend during sorting
  }

  cleanRowEditingData(row, data) {
    // Called before Row data is sent to backend during editing
  }

  ///////////////////////////////////

  createObservables(data, node) {
    // Map all row data
    this.mapAllRows(data, node);
    // Map all columns
    this.mapAllColumns(data, node);
    if (this.config.table_properties.timezone) {
      this.timezone = ko.observable(moment(data.timezone()).format(Kadenze.I18n.t('date.formats.js_timezone_code')));
    }
    if (this.config.column_properties.hideable.enabled) {
      this.dataTableName = _.isUndefined(this.config.table_properties.name) ? '' : this.config.table_properties.name;
      this.colOptsPanelAnimateProps = ko.observable({ top: 0, height: 0, autoAlpha: 0, display: 'none' });
      this.hideColumnOptionsPanel = ko.observable(true);
      this.visibleColsChanged = ko.observable(false);
    }
    if (this.config.table_properties.paginatable.enabled) {
      this.paginationOptions = ['10', '25', '50', '100'];
      this.selectedPer =  initialPerIndex == -1 ? ko.observable(this.paginationOptions[initialPerIndex]) : ko.observable(this.paginationOptions[0]);
      let initialPerIndex = data['selected_per'] ? _.indexOf(this.paginationOptions, data['selected_per']()) : 0;
      this.selectedPage = ko.observable(1);
      this.firstPage = data['first_page'];
      this.lastPage = data['last_page'];
    }

    if (this.config.row_properties.draggable.enabled) {
      this.rowsDraggable = ko.observable(true);
      this.orderList = data[`${this.config.dataset_name}_ids`]().map(n => n.toString());
    }

    if (this.config.column_properties.editable.enabled) {
      this.editingType = ko.observable('');
      this.tempOldRowVal = ko.observable('');
      this.lastChangedCol = ko.observable('');
    }
    this.$mobileModalParent = $('#mobile_modal_parent');
    this.$mobileModalTableOpts = $('#mobile_modal_table_options');
    this.mobileModal = ko.observable(false);
  }

  mapAllColumns(data, node) {
    const columnMapping = {
      create: col => new Column(col.data, this, node, this.config.column_properties)
    };

    this.columns = ko.mapping.fromJS(this.config.columns, columnMapping);
  }

  mapAllRows(data, node) {
    this.rowMapping = {
      create: row => new Row(row.data, this, node, this.config.row_properties)
    };

    this.rows = ko.mapping.fromJS(data[this.config.dataset_name], this.rowMapping);
  }

  initDataTableUI() {
    if (this.config.row_properties.draggable.enabled && this.rows().length)
      this.toggleDraggability();

    this.setMobileScrollLoadEvent();
  }

  getRowData(url, params={}) {
    let requestParams = {
      url: url,
      type: 'GET'
    };

    if (params){
      requestParams.data = params;
      requestParams.dataAsJSON = false;
    }

    let request = new Kadenze.AjaxRequest(requestParams);

    request.done(data => {
      const mappedRows = ko.mapping.fromJS(
        ko.mapping.fromJS(data[this.config.dataset_name]),
        this.rowMapping
      );

      this.rows(mappedRows());
      this.firstPage(data['first_page']);
      this.lastPage(data['last_page']);
      this.totals(ko.mapping.fromJS(data[`total_${this.config.dataset_name}`])());
      this.resortOrResetCols();

      Kadenze.UIUtil.hideLoadingIndicator(this.$el);
    });

    request.fail(({ message }) => {
      Kadenze.UIUtil.hideLoadingIndicator(this.$el);
      new Kadenze.Notice.error({ message });
    });
  }

  nextPage(){
    if (!this.lastPage())
      this.selectedPage(this.selectedPage() + 1);
  }

  previousPage(){
    if (!this.firstPage())
      this.selectedPage(this.selectedPage() - 1);
  }

  paginationChanged(goToFirstPage=false){
    Kadenze.UIUtil.showLoadingIndicator(this.$el);

    if (goToFirstPage)
      this.selectedPage(1);

    const params = {
      page: this.selectedPage(),
      per: this.selectedPer()
    };

    this.getRowData(this.config.row_properties.data_url, params);
  }

  setMobileScrollLoadEvent() {
    $(window).scroll(() => {
      const scrollTop = $(window).scrollTop();
      const windowHeight = $(window).height();

      if (
        ((scrollTop + windowHeight) == $(document).height()) &&
          (this.windowWidth() < this.mobileBreakPoint) &&
            !this.lastPage()
      ) {
        const oldPer = parseInt(this.selectedPer());
        this.selectedPer(oldPer + 10);
      }
    });
  }

  toggleDraggability() {
    if (this.rowsDraggable()){
      this.$tbody.sortable('enable');
    } else {
      this.$tbody.sortable('disable');
    }
  }

  toggleColumnOptionsPanel() {
    if (this.hideColumnOptionsPanel()){
      this.colOptsPanelAnimateProps({
        top: 96,
        height: 170,
        autoAlpha: 1,
        display: 'block'
      });

      this.hideColumnOptionsPanel(false);
    } else {
      this.colOptsPanelAnimateProps({
        top: 0,
        height: 0,
        autoAlpha: 0,
        display: 'none'
      });

      this.hideColumnOptionsPanel(true);

      if (this.visibleColsChanged())
        this.saveVisibleCols();
    }
  }

  toggleParentModal() {
    if (this.mobileModal()) {

      this.$mobileModalParent.modal('hide');
      this.mobileModal(false);

      if (this.visibleColsChanged())
        this.saveVisibleCols();

    } else {
      this.$mobileModalParent.modal('show');
      this.mobileModal(true);
    }
  }

  resortOrResetCols() {
    const visibleCols = _.reject(this.columns(), col => _.isUndefined(col.sortable.sorting));
    const sortedCol = _.find(visibleCols, col => col.sortable.sorting());

    if (!_.isUndefined(sortedCol)){
      ko.bindingHandlers.sortableCol.sortCols(sortedCol, this);
    } else {
      ko.bindingHandlers.sortableCol.sortByResetCol(this);
    }
  }

  setUserOptions() {
    Kadenze.UserState.get(userState => {
      const storedCols = userState[`${this.dataTableName}_columns`];

      let visibleCols = this.config.column_properties.hideable.default_columns;

      if (typeof storedCols !== 'undefined')
        visibleCols = storedCols['cols'];

      if (typeof visibleCols !== 'undefined') {
        const targetCols = this.columns().filter(
          col => visibleCols.includes(col.property)
        );

        targetCols.forEach(col => col.visible(true))
      }
    });
  }

  saveVisibleCols() {
    const visibleCols = _.pluck(
      _.reject(this.columns(), col => !col.visible()),
      'property'
    );

    Kadenze.UserState.set({
      [`${this.dataTableName}_columns`]: { cols: visibleCols }
    })

    this.visibleColsChanged(false);
  }

  showTableOptsModal() {
    this.$mobileModalParent.modal('hide');
    this.$mobileModalTableOpts.modal('show');
  }

  getFixedGroupWidth() {
    return 'kd-data-table__column-fixed-group-' + this.actions();
  }

  getActionsColWidthClass() {
    return 'kd-data-table__column-actions-' + this.actions();
  }

  paginationText() {
    return ko.pureComputed(() => {
      let min, max;

      if (this.selectedPage() == 1) {
        min = 1;
        max = this.selectedPer();
      } else {
        min = this.selectedPer() * (this.selectedPage() - 1);
        max = parseInt(min) + parseInt(this.selectedPer());
      }

      if (max > this.totals()){
        return `${min}-${this.totals()} of ${this.totals()}`;
      } else {
        return `${min}-${max} of ${this.totals()}`;
      }
    }, this);
  }

  setupSubscriptions() {
    this.mobileBreakPoint = Kadenze.UIUtil.mobileBreakPoint();
    this.$window = $(window);
    this.windowWidth = ko.observable(this.$window.width());

    this.selectedPer.subscribe(value => {
      if (!this.firstPage()) {
        this.paginationChanged(true);
      } else {
        this.paginationChanged();
      }

      Kadenze.UserState.set({
        [`${this.dataTableName}_per`]: { value }
      })
    });

    this.selectedPage.subscribe(() => {
      this.paginationChanged();
    });

    if (this.config.column_properties.editable.enabled) {
      if (this.windowWidth() < this.mobileBreakPoint) {
        this.canEdit = ko.observable(false);
      } else {
        this.canEdit = ko.observable(true);
      }
    }

    this.$window.resize(() => this.windowWidth(this.$window.width()))

    this.windowWidth.subscribe(value => {
      if (!this.config.column_properties.editable.enabled)
        return;

      if (value < this.mobileBreakPoint) {
        this.canEdit(false);
      } else {
        this.canEdit(true);
      }
    });
  }
}
